// Bulk salaries (HR/owner). Download a roster (employee_id, name, email,
// department, monthly_salary), fill in salaries, upload → for each row assign to
// payroll if needed + revise the base salary. Reuses the per-employee payroll
// endpoints client-side (no new bulk backend), with a progress bar + per-row
// results.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wallet, Download, Upload, Loader2, ShieldAlert, CheckCircle2, Play,
} from 'lucide-react';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { getAllEmployees, getDepartments, type Employee } from '@/lib/hrApi';
import {
  setCompensation, assignToPayroll, listLegalEntities, type LegalEntityLite,
} from '@/lib/compensationApi';
import { parseCSV } from '@/lib/csv';

const todayIso = new Date().toISOString().slice(0, 10);
const norm = (s?: string) => (s || '').trim().toLowerCase();

interface Row {
  email: string;
  name: string;
  salary: string;
  recordId?: string;          // matched employee UUID
  nationalId?: string;
  status?: 'pending' | 'set' | 'assigned' | 'skipped' | 'error';
  detail?: string;
}

/** Concurrency-limited runner (keeps the API from being hammered). */
async function runLimited<T>(items: T[], limit: number, fn: (it: T, i: number) => Promise<void>, onTick: () => void) {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      try { await fn(items[i], i); } finally { onTick(); }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

export const BulkSalariesPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const { isTenantOwner, isHrManager } = useRoleAccess();
  const canManage = isTenantOwner || isHrManager;

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [deptNames, setDeptNames] = useState<Record<string, string>>({});
  const [entities, setEntities] = useState<LegalEntityLite[]>([]);
  const [entityId, setEntityId] = useState('');
  const [effFrom, setEffFrom] = useState(todayIso);
  const [rows, setRows] = useState<Row[]>([]);
  const [fileName, setFileName] = useState('');
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    if (!canManage) return;
    (async () => {
      const [emps, depts, ents] = await Promise.all([
        getAllEmployees().catch(() => [] as Employee[]),
        getDepartments().catch(() => ({ items: [] as any[] })),
        listLegalEntities().catch(() => [] as LegalEntityLite[]),
      ]);
      setEmployees(emps);
      const dn: Record<string, string> = {};
      ((depts as any).items || []).forEach((d: any) => { dn[d.id] = d.name; });
      setDeptNames(dn);
      setEntities(ents);
      if (ents[0]) setEntityId(ents[0].id);
    })();
  }, [canManage]);

  const byEmail = useMemo(() => {
    const m = new Map<string, Employee>();
    employees.forEach((e) => { if (e.email) m.set(norm(e.email), e); });
    return m;
  }, [employees]);

  const downloadRoster = () => {
    const header = ['employee_id', 'full_name', 'email', 'department', 'monthly_salary'];
    const lines = employees.map((e) =>
      [
        e.employee_id || '',
        `${e.first_name || ''} ${e.last_name || ''}`.trim(),
        e.email || '',
        deptNames[e.department_id || ''] || '',
        '',
      ].map((v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)).join(','),
    );
    const csv = [header.join(','), ...lines].join('\n') + '\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'salaries-roster.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (file: File) => {
    setNote(null); setDone(false);
    try {
      const { rows: parsed } = parseCSV(await file.text());
      const mapped: Row[] = parsed
        .map((r) => {
          const email = norm(r.email || r.employee_email);
          const salary = String(r.monthly_salary || r.salary || '').replace(/[^0-9.]/g, '');
          const emp = byEmail.get(email);
          return {
            email,
            name: emp ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim() : (r.full_name || email),
            salary,
            recordId: emp?.id,
            nationalId: (emp as any)?.national_id,
            status: !salary ? 'skipped' : !emp ? 'error' : 'pending',
            detail: !email ? t('bulk_salaries.detail_no_email') : !byEmail.get(email) ? t('bulk_salaries.detail_no_match') : !salary ? t('bulk_salaries.detail_no_salary') : undefined,
          } as Row;
        })
        .filter((r) => r.email);
      setRows(mapped);
      setFileName(file.name);
    } catch {
      setNote({ kind: 'err', text: t('bulk_salaries.read_error') });
    }
  };

  const toApply = rows.filter((r) => r.status === 'pending' && r.recordId && r.salary);

  const apply = async () => {
    setApplying(true); setProgress(0); setNote(null);
    const work = [...toApply];
    await runLimited(work, 4, async (row) => {
      try {
        try {
          await setCompensation(row.recordId!, { amount: row.salary, effective_from: effFrom });
          row.status = 'set';
        } catch (e: any) {
          if (e?.response?.status === 404) {
            // Not on payroll yet → assign to the chosen legal entity, then set.
            await assignToPayroll(row.recordId!, {
              legal_entity_id: entityId, full_name: row.name, email: row.email, national_id: row.nationalId,
            });
            await setCompensation(row.recordId!, { amount: row.salary, effective_from: effFrom });
            row.status = 'assigned';
          } else {
            throw e;
          }
        }
      } catch (e: any) {
        row.status = 'error';
        row.detail = e?.response?.data?.message || t('bulk_salaries.detail_failed');
      }
      setRows((rs) => [...rs]); // reflect per-row status
    }, () => setProgress((p) => p + 1));
    setApplying(false);
    setDone(true);
    const ok = work.filter((r) => r.status === 'set' || r.status === 'assigned').length;
    setNote({ kind: 'ok', text: t('bulk_salaries.updated_summary', { ok, total: work.length }) });
  };

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('bulk_salaries.no_permission_title')}</h1>
        <p className="text-muted-foreground mt-1">{t('bulk_salaries.no_permission_body')}</p>
      </div>
    );
  }

  const input = 'h-10 px-3 rounded-lg border border-border bg-card text-foreground text-sm focus:border-orange-400 focus:outline-none';
  const primaryBtn = 'h-10 px-4 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50';
  const ghostBtn = 'h-10 px-4 rounded-lg border border-border text-foreground hover:bg-muted inline-flex items-center gap-2';
  const STATUS_BADGE: Record<string, string> = {
    set: 'bg-info-soft text-info-strong', assigned: 'bg-success-soft text-success-strong',
    skipped: 'bg-muted text-muted-foreground', error: 'bg-danger-soft text-danger-strong', pending: 'bg-warning-soft text-warning-strong',
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2"><Wallet className="w-6 h-6" /> {t('bulk_salaries.title')}</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          {t('bulk_salaries.subtitle')}
        </p>
      </div>

      {note && (
        <div className={`rounded-lg px-3 py-2 text-sm border ${note.kind === 'ok' ? 'bg-success-soft border-success text-success-strong' : 'bg-danger-soft border-danger text-danger-strong'}`}>{note.text}</div>
      )}

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-center gap-3">
        <button className={ghostBtn} onClick={downloadRoster} disabled={employees.length === 0}>
          <Download className="w-4 h-4" /> {t('bulk_salaries.download_roster', { count: employees.length })}
        </button>
        <label className={`${ghostBtn} cursor-pointer`}>
          <Upload className="w-4 h-4" /> {t('bulk_salaries.upload_filled')}
          <input type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
        </label>
        {fileName && <span className="text-sm text-muted-foreground">{t('bulk_salaries.file_summary', { file: fileName, count: rows.length })}</span>}
      </div>

      {rows.length > 0 && (
        <>
          <div className="rounded-2xl border border-border bg-card p-4 flex flex-wrap items-end gap-3">
            <label>
              <span className="block text-xs text-muted-foreground mb-1">{t('bulk_salaries.legal_entity')}</span>
              <select className={input} value={entityId} onChange={(e) => setEntityId(e.target.value)}>
                {entities.length === 0 && <option value="">{t('bulk_salaries.no_legal_entity')}</option>}
                {entities.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
              </select>
            </label>
            <label>
              <span className="block text-xs text-muted-foreground mb-1">{t('bulk_salaries.effective_from')}</span>
              <input className={input} type="date" value={effFrom} onChange={(e) => setEffFrom(e.target.value)} />
            </label>
            <div className="flex-1" />
            <button className={primaryBtn} onClick={apply} disabled={applying || toApply.length === 0}>
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {applying ? t('bulk_salaries.applying', { progress, total: toApply.length }) : t('bulk_salaries.apply_count', { count: toApply.length })}
            </button>
          </div>

          <div className="rounded-2xl border border-border bg-card p-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">{t('bulk_salaries.table.employee')}</th>
                    <th className="text-left font-medium px-3 py-2">{t('bulk_salaries.table.email')}</th>
                    <th className="text-right font-medium px-3 py-2">{t('bulk_salaries.table.salary')}</th>
                    <th className="text-left font-medium px-3 py-2 w-28">{t('bulk_salaries.table.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r, i) => (
                    <tr key={i} className="text-foreground">
                      <td className="px-3 py-2">{r.name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.email}</td>
                      <td className="px-3 py-2 text-right">{r.salary ? Number(r.salary).toLocaleString('en-US') : '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status || 'pending']}`}>{r.status}</span>
                        {r.detail && <span className="text-xs text-muted-foreground ml-1">{r.detail}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {done && (
            <div className="flex items-center gap-2 text-sm text-success"><CheckCircle2 className="w-4 h-4" /> {t('bulk_salaries.done')}</div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkSalariesPage;
